/**
 * PURPOSE: Helpers puros de la edición inline y masiva de la tabla de Programación (FTR-0007).
 * RESPONSIBILITIES: Sembrar el borrador editable, detectar filas/campos sucios, armar el
 *   payload del lote atómico y el de la actualización masiva de jornada, y resolver la
 *   selección de partidos de esa acción masiva (FTR-0033).
 * COLLABORATORS: ProgramacionArea.jsx,
 *   backend PATCH /patchprogramacionmatchesbatch y PATCH /patchprogramacionjornada.
 * ROUTE: winscore/src/utils/programacionInlineEdit.js
 *
 * Solo fecha, hora y los dos uniformes visibles en la tabla son editables inline.
 * La sede por fila es de solo lectura (se cambia por el modal o por la acción masiva).
 */

// campo del API -> columna del match recibido por getmatches
//
// FTR-0028 WG6: `idcancha` se añade al final. La sede sigue fuera (regla de FTR-0007). Es inofensivo
// con el Centro apagado: `getmatches` no devuelve entonces la columna, el borrador la siembra como
// "" y `computeDirtyEntries` solo envía lo que CAMBIÓ, así que sin el selector de cancha en pantalla
// el campo nunca viaja y el lote es idéntico al de hoy.
export const INLINE_FIELDS = [
  { field: "date", column: "date_match" },
  { field: "time", column: "time_match" },
  { field: "uniforme1", column: "uniforme1" },
  { field: "uniforme2", column: "uniforme2" },
  { field: "idcancha", column: "idcancha" },
];

/** "HH:mm:ss" -> "HH:mm" (el input type="time" trabaja en HH:mm); vacío/null -> "". */
export const normalizeTimeValue = (value) => {
  if (typeof value !== "string") return "";
  const trimmed = value.trim();
  if (trimmed === "") return "";
  const matchHms = trimmed.match(/^(\d{2}:\d{2})(:\d{2})?$/);
  return matchHms ? matchHms[1] : trimmed;
};

const toInputValue = (field, raw) => {
  if (raw === null || raw === undefined) return "";
  return field === "time" ? normalizeTimeValue(String(raw)) : String(raw);
};

/** Borrador inicial: { [idmatch]: { date, time, uniforme1, uniforme2 } } (valores de input). */
export const buildInlineDraft = (matches = []) => {
  const draft = {};
  for (const match of matches) {
    if (!match?.idmatch) continue;
    const row = {};
    for (const { field, column } of INLINE_FIELDS) {
      row[field] = toInputValue(field, match[column]);
    }
    draft[match.idmatch] = row;
  }
  return draft;
};

const sameValue = (field, original, edited) =>
  toInputValue(field, original) === toInputValue(field, edited);

/**
 * Entradas sucias para el lote atómico: solo partidos con cambios y, dentro de cada
 * partido, solo los campos que cambiaron ("" viaja como "" y el backend lo guarda NULL).
 */
export const computeDirtyEntries = (matches = [], draft = {}) => {
  const entries = [];
  for (const match of matches) {
    const row = draft[match?.idmatch];
    if (!row) continue;
    const entry = { idmatch: match.idmatch };
    let dirty = false;
    for (const { field, column } of INLINE_FIELDS) {
      if (row[field] === undefined) continue;
      if (!sameValue(field, match[column], row[field])) {
        entry[field] = row[field];
        dirty = true;
      }
    }
    if (dirty) entries.push(entry);
  }
  return entries;
};

export const isFieldDirty = (match, row, field) => {
  if (!match || !row || row[field] === undefined) return false;
  const column = INLINE_FIELDS.find((f) => f.field === field)?.column;
  if (!column) return false;
  return !sameValue(field, match[column], row[field]);
};

export const hasInlineChanges = (matches, draft) =>
  computeDirtyEntries(matches, draft).length > 0;

/**
 * FTR-0033: selección de partidos de la acción masiva (checkbox junto al N° en "Editar tabla").
 * Descarta ids que ya no están en la jornada visible; 0 o todos seleccionados => null (alcance
 * completo, que resuelve el backend); selección parcial => lista de idmatch en el orden de la
 * jornada. La lista solo restringe el alcance del servidor, nunca lo amplía.
 */
export const resolveBulkSelection = (selectedIds = [], jornadaMatches = []) => {
  const selected = new Set((selectedIds || []).map(Number));
  const jornadaIds = [...new Set((jornadaMatches || []).map((match) => Number(match?.idmatch)))].filter(
    (id) => Number.isInteger(id) && id > 0
  );
  const selectedInJornada = jornadaIds.filter((id) => selected.has(id));

  if (selectedInJornada.length === 0 || selectedInJornada.length === jornadaIds.length) return null;
  return selectedInJornada;
};

/**
 * Payload de la actualización masiva. `includePhase` replica exactamente la regla con la
 * que la vista pide getmatches (shouldUseOldNoPhaseFilter): si la consulta de la jornada
 * no filtra por fase, la masiva tampoco. Fecha en blanco / sede sin seleccionar = "no cambiar".
 * `idmatches` (FTR-0033) solo viaja con una selección parcial (ver resolveBulkSelection).
 * Devuelve { payload } o { error }.
 */
export const buildBulkJornadaPayload = ({
  idevent,
  idsport,
  idtypephase,
  includePhase,
  nroFecha,
  date,
  idsede,
  idmatches = null,
}) => {
  const cleanDate = typeof date === "string" ? date.trim() : "";
  const cleanSede = Number(idsede) > 0 ? Number(idsede) : null;

  if (!cleanDate && !cleanSede) {
    return { error: "Debes indicar una fecha y/o una sede para aplicar a la jornada." };
  }

  const payload = {
    idevent: Number(idevent),
    idsport: Number(idsport),
    nro_fecha: Number(nroFecha),
  };
  if (includePhase) payload.idtypephase = Number(idtypephase);
  if (cleanDate) payload.date = cleanDate;
  if (cleanSede) payload.idsede = cleanSede;
  if (Array.isArray(idmatches) && idmatches.length) payload.idmatches = idmatches.map(Number);

  return { payload };
};
